/**
 * <s-sticky-atc> — phone-only sticky Add to cart bar.
 * Slides up once the product form's buttons scroll out of view (above the viewport),
 * and hides again when they're back. Its button clicks the real Add to cart button
 * inside the product form (so variant, custom fit and extra lines all apply); when the
 * product is sold out (no real button) it scrolls back to the options instead.
 *
 * Attributes:
 *   data-form-root — id of the <s-product-form> element.
 *   data-anchor    — id of the element marking the bottom of the buy buttons.
 */
class SStickyAtc extends HTMLElement {
  #io;
  #ac;
  #mo;

  connectedCallback() {
    this.#ac = new AbortController();
    const anchor = document.getElementById(this.dataset.anchor || '');
    if (anchor && 'IntersectionObserver' in window) {
      this.#io = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggleAttribute('data-visible', passed);
        this.inert = !passed;
      });
      this.#io.observe(anchor);
    }
    this.inert = true;

    // Follow the selected size: the product-form partial re-renders a
    // [data-sticky-atc-state] carrier on every variant change.
    const root = document.getElementById(this.dataset.formRoot || '');
    if (root) {
      this.#sync(root);
      this.#mo = new MutationObserver(() => this.#sync(root));
      this.#mo.observe(root, { childList: true, subtree: true });
    }

    this.querySelector('[data-sticky-atc-button]')?.addEventListener('click', () => {
      const root = document.getElementById(this.dataset.formRoot || '');
      const realButton = root?.querySelector('button[name="add"]:not([data-buy-now])');
      if (realButton && realButton.getAttribute('aria-disabled') !== 'true') {
        realButton.click();
      } else {
        root?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, { signal: this.#ac.signal });
  }

  #sync(root) {
    const state = root.querySelector('[data-sticky-atc-state]');
    if (!state) return;
    const price = this.querySelector('[data-sticky-atc-price]');
    const size = this.querySelector('[data-sticky-atc-size]');
    const button = this.querySelector('[data-sticky-atc-button]');
    if (price && state.dataset.price && price.textContent !== state.dataset.price) price.textContent = state.dataset.price;
    if (size && size.textContent !== (state.dataset.size || '')) size.textContent = state.dataset.size || '';
    if (button) {
      const label = state.dataset.available === 'true' ? button.dataset.addLabel : button.dataset.soldOutLabel;
      if (label && button.textContent.trim() !== label) button.textContent = label;
    }
  }

  disconnectedCallback() {
    this.#mo?.disconnect();
    this.#io?.disconnect();
    this.#ac?.abort();
  }
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
